/**
 * Progress Context
 * 
 * Lightweight progress tracking for operations with toast notifications.
 * This replaces the old toast-based progress tracking which was prone to 
 * getting stuck.
 */

import { createContext, useContext, useCallback, ReactNode } from 'react';
import toast from 'react-hot-toast';

// Initial resource snapshot (kept for compatibility)
export interface ResourceSnapshot {
  vpcId?: string;
  subnetId?: string;
  securityGroupId?: string;
  routeTableId?: string;
  internetGatewayId?: string;
  instanceId?: string;
}

// Simplified progress state (just for tracking active operations)
export interface CancelProgress {
  jobId: string;
  currentStep: number;
  stepLabel: string;
  percentage: number;
  status: 'in_progress' | 'completed' | 'failed' | 'skipped';
  message?: string;
  networkPreserved?: boolean;
  startTime: number;
  hidden?: boolean;
  initialResources?: ResourceSnapshot;
  removedResources?: {
    instance: boolean;
    eni: boolean;
    securityGroup: boolean;
    subnet: boolean;
    routeTable: boolean;
    internetGateway: boolean;
    vpc: boolean;
  };
  lastPollTime?: number;
  pollError?: string;
}

// Context interface - simplified
interface ProgressContextType {
  progress: Map<string, CancelProgress>;
  startProgress: (jobId: string, initialResources?: ResourceSnapshot) => void;
  completeProgress: (jobId: string, success: boolean, message: string, networkPreserved?: boolean) => void;
  hideProgress: (jobId: string) => void;
}

const ProgressContext = createContext<ProgressContextType | undefined>(undefined);

export function ProgressProvider({ children }: { children: ReactNode }) {
  // Simplified progress tracking with toast notifications
  const progress = new Map<string, CancelProgress>();

  const startProgress = useCallback((jobId: string, _initialResources?: ResourceSnapshot) => {
    // Show a simple toast notification
    toast.success('Operation started', {
      duration: 3000,
      icon: '🚀',
      style: {
        background: '#18181b',
        color: '#fafafa',
        border: '1px solid #3f3f46',
      },
    });
    console.log(`[Progress] Operation started for ${jobId}`);
  }, []);

  const completeProgress = useCallback((jobId: string, success: boolean, message: string, networkPreserved?: boolean) => {
    // Show success/failure toast
    if (success) {
      toast.success(
        <div>
          <p className="font-medium">{networkPreserved ? 'Operation completed' : 'Operation completed'}</p>
          <p className="text-sm opacity-80 mt-1">{message}</p>
        </div>,
        { duration: 4000 }
      );
    } else {
      toast.error(
        <div>
          <p className="font-medium">Operation failed</p>
          <p className="text-sm opacity-80 mt-1">{message}</p>
        </div>,
        { duration: 6000 }
      );
    }
    console.log(`[Progress] Operation ${jobId} ${success ? 'completed' : 'failed'}: ${message}`);
  }, []);

  const hideProgress = useCallback((_jobId: string) => {
    // No-op since we're not showing progress toasts anymore
    toast.dismiss();
  }, []);

  return (
    <ProgressContext.Provider value={{ progress, startProgress, completeProgress, hideProgress }}>
      {children}
    </ProgressContext.Provider>
  );
}

export function useProgress() {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
}
